import { spendingSign, type Category, type Transaction } from './model';

/**
 * The one place that turns entries into spending (brief §14–15). Every
 * screen that shows a spending figure goes through here, so a rule change
 * (what counts, what subtracts) lands everywhere at once.
 *
 *  - expense, debt payment: count;
 *  - refund: subtracts;
 *  - credit card bill: counts only when marked "Count as spending";
 *  - transfer, income: never spending.
 *
 * Loan payments are ordinary spending in the Financial obligations group,
 * so they sit next to rent and groceries in every total and breakdown.
 */
export type SpendingGroup = 'fixed' | 'variable' | 'obligations';
export const SPENDING_GROUPS: readonly SpendingGroup[] = ['fixed', 'variable', 'obligations'];

export function groupOf(category: Pick<Category, 'kind' | 'nature'> | undefined): SpendingGroup {
  if (category?.kind === 'obligation') return 'obligations';
  return category?.nature === 'fixed' ? 'fixed' : 'variable';
}

/** Signed contribution of one entry to spending, in its minor units. */
export function spendingOf(e: Pick<Transaction, 'type' | 'countsAsSpending' | 'amountMinor'>): number {
  return spendingSign(e) * e.amountMinor;
}

export interface SpendingSummary {
  total: number;
  byGroup: Record<SpendingGroup, number>;
  /** Categories with spending, largest first. */
  byCategory: { categoryId: string; total: number }[];
}

/** What a summary needs of an entry; per-category sums from SQL (Home) are enough too. */
export type SpendingItem = Pick<Transaction, 'categoryId' | 'type' | 'countsAsSpending' | 'amountMinor'>;

export function summarizeSpending(entries: readonly SpendingItem[], categories: ReadonlyMap<string, Category>): SpendingSummary {
  const byGroup: Record<SpendingGroup, number> = { fixed: 0, variable: 0, obligations: 0 };
  const byCat = new Map<string, number>();
  let total = 0;
  for (const e of entries) {
    const v = spendingOf(e);
    if (v === 0) continue;
    total += v;
    byGroup[groupOf(categories.get(e.categoryId))] += v;
    byCat.set(e.categoryId, (byCat.get(e.categoryId) ?? 0) + v);
  }
  const byCategory = [...byCat]
    .filter(([, v]) => v > 0)
    .map(([categoryId, v]) => ({ categoryId, total: v }))
    .sort((a, b) => b.total - a.total);
  return { total, byGroup, byCategory };
}
